import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  Button,
  CollapsePanel,
  CurrencySelect,
  Icon,
} from "../../components/common";
import { ArchwayDetails } from "../../components/forms/invoice/ArchwayDetails";
import { NewBankAccountModal, NewWalletModal } from "../../components/modals";
import { WalletDropdown } from "../../components/wallet";
// import DashboardLayout from "../../layouts/Dashboard.layout";
import { selectOnramps } from "../../store/reducers/onramps";
import { selectCurrentUser } from "../../store/reducers/user";
import { selectWallets } from "../../store/reducers/wallets";
import {
  useGetOnrampsQuery,
  useUpdateOnrampMutation,
} from "../../store/services/onramps";
import { useGetWalletsQuery } from "../../store/services/wallets";
import { Currency, CurrencyType } from "../../types/InvoiceTypes";
import { Wallet } from "../../types/WalletTypes";
import { toastSuccess } from "../../utils/toasts";
import {
  //AcceptTosUsdBanner,
  GetVerifiedUsdBanner,
} from "../../components/banners";
import { CURRENCY_MAP } from "../../constants";
import { useAllowedChains } from "../../hooks/useAllowedChains";
import TestLayout from "../../layouts/Test.layout";

export const USDPage: FC = () => {
  // const [selectedFiatCurrency, setSelectedFiatCurrency] = useState<Currency>(
  //   Currency.USD
  // );
  const selectedFiatCurrency = Currency.USD;
  const {
    usd_tos_accepted,
    usd_kyc_complete_and_approved,
    allowed_outgoing_currencies,
    is_onmeta_eligible,
  } = useSelector(selectCurrentUser);

  const [selectedOutCurrency, setSelectedOutCurrency] = useState<Currency>(
    //DEFAULT_CRYPTO_CURRENCY
    allowed_outgoing_currencies[0]
  );
  const [outCurrencyChanged, setOutCurrencyChanged] = useState(false);

  const [selectedWallet, setSelectedWallet] = useState<Wallet>();
  useGetOnrampsQuery();
  const onramps = useSelector(selectOnramps);
  useGetWalletsQuery();
  const walletList = useSelector(selectWallets);
  const [updateOnramp] = useUpdateOnrampMutation();
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);

  const [showKycModal, setShowKycModal] = useState(false);
  //const [showTosModal, setShowTosModal] = useState(false);

  const { refetch } = useGetOnrampsQuery();

  const allowedChains = useAllowedChains(selectedOutCurrency);
  const allowedWallets = useMemo(
    () => walletList.filter(({ chain }) => allowedChains.includes(chain!)),
    [walletList, allowedChains]
  );

  const scrollRef = useRef<HTMLHeadingElement>(null);
  const scrollToWallets = () => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (onramps) {
      const foundWallet = allowedWallets?.find(
        ({ id }) => id === onramps.wallet_id
      );

      if (foundWallet) {
        setSelectedWallet(foundWallet);
      }
      if (!outCurrencyChanged && (onramps.outgoing_currency as Currency)) {
        setSelectedOutCurrency(onramps.outgoing_currency as Currency);
      }
    }
  }, [onramps, allowedWallets, selectedOutCurrency, outCurrencyChanged]);

  const selectedPaymentDetails = onramps?.bank_details?.find(
    (detail) => detail.currency === selectedFiatCurrency
  );

  const onSelectWallet = (wallet: Wallet) => {
    updateOnramp({
      wallet_id: wallet.id!,
      outgoing_currency: selectedOutCurrency,
    });
  };

  // const CurrencySelector = (
  //   <CurrencySelect
  //     name="incoming_currency"
  //     allowedCurrencies={[Currency.USD]}
  //     value={selectedFiatCurrency}
  //     currencyType={CurrencyType.FIAT}
  //     onChange={(e: ChangeEvent<HTMLInputElement>) => {
  //       setSelectedFiatCurrency(e.target.value as Currency);
  //     }}
  //   />
  // );

  const paymentDetailsMessage = useMemo(() => {
    return (
      <>
        Only send{" "}
        <span className="font-extrabold uppercase">{selectedFiatCurrency}</span>{" "}
        via <span className="font-extrabold">ACH/Wire</span> to these bank
        details.{" "}
        <span className="font-extrabold">IMPORTANT: NO SWIFT Payments!</span>
      </>
    );
  }, [selectedFiatCurrency]);

  const textCurrency = useMemo(() => {
    return is_onmeta_eligible ? "INR or USDC" : "USDC";
  }, [is_onmeta_eligible]);

  const showBankDetails = !!selectedPaymentDetails?.account_number;

  const onCreateWallet = () => {
    if (CURRENCY_MAP[selectedOutCurrency].type === CurrencyType.CRYPTO) {
      setShowWalletForm(true);
      setShowBankAccountForm(false);
    } else {
      setShowWalletForm(false);
      setShowBankAccountForm(true);
    }
  };

  return (
    <TestLayout
      title="Your USD Account"
      //action={CurrencySelector}
      containerClassName="flex flex-col !p-0"
    >
      <div className="mb-4 md:mb-16 bg-white px-4 md:px-1 pt-2 md:pt-2 pb-8 md:pb-16 flex flex-col gap-3">
        {/* <h4 className="text-gray-500 font-source-serif text-3xl pb-2">
          Open Your USD Account Today!
        </h4> */}
        <p className="mt-2">
          Open a virtual USD account to seamlessly receive payments from clients
          in the US. We automatically convert your USD into{" "}
          <b>{textCurrency}</b> at competitive exchange rates, then deposit it
          directly into your chosen wallet or bank account.
        </p>

        <h5 className="font-manrope font-extrabold text-base pt-6">
          🚀 How it works:
        </h5>
        <ul className="list-disc list-inside indent-1">
          <li>
            <b>Open Your Account</b> - Click the button below to verify your
            identity online and get your USD account details.
          </li>
          <li>
            <b>Receive Payments</b> - Use your USD account details to withdraw
            funds from popular platforms (
            <b>Upwork, Deel, PayPal, Fiverr, etc.</b>).
          </li>
          <li>
            <b>Automatic Conversion</b> - Your USD funds are automatically
            converted to your selected currency (<b>{textCurrency}</b>) and
            transferred to your linked wallet or bank account.
          </li>
        </ul>
        <p className="mt-2">Getting started is simple...</p>
        {(!usd_kyc_complete_and_approved || !usd_tos_accepted) && (
          <div className="flex">
            <GetVerifiedUsdBanner
              show={showKycModal}
              setShow={setShowKycModal}
            />
          </div>
        )}
        {usd_kyc_complete_and_approved && !selectedWallet && (
          <div className="flex">
            <Button
              className="px-5"
              contentClassName="flex gap-5"
              variant={Button.variant.PRIMARY}
              onClick={scrollToWallets}
            >
              <Icon
                icon="Arrow-right"
                size={24}
                className="text-white"
                disableFill
              />
              Choose your payout destination below{" "}
            </Button>
          </div>
        )}
        {usd_kyc_complete_and_approved &&
          selectedWallet &&
          !showBankDetails && (
            <div className="flex">
              <Button
                className="px-5"
                contentClassName="flex gap-5"
                variant={Button.variant.TEMPTING}
                onClick={refetch}
              >
                <Icon
                  icon="Arrow-right"
                  size={24}
                  className="text-white"
                  disableFill
                />
                Show USD account details{" "}
              </Button>
            </div>
          )}

        {showBankDetails && (
          <ArchwayDetails
            show={!!selectedPaymentDetails}
            currency={selectedFiatCurrency}
            message={paymentDetailsMessage}
            data={{
              ...selectedPaymentDetails,
            }}
            removeReference={true}
          />
        )}
        <h5
          ref={scrollRef}
          className="font-manrope font-extrabold text-base pt-6"
        >
          Once received, we convert your USD into:
        </h5>
        <CurrencySelect
          name="outgoing_currency"
          allowedCurrencies={allowed_outgoing_currencies}
          value={selectedOutCurrency}
          //currencyType={CurrencyType.FIAT}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectedWallet(undefined);
            setSelectedOutCurrency(e.target.value as Currency);
            setOutCurrencyChanged(true);
          }}
        />
        <h5 className="font-manrope font-extrabold text-base pt-6">
          and send it to:
        </h5>

        <WalletDropdown
          values={allowedWallets}
          value={selectedWallet}
          onSelect={onSelectWallet}
          type={
            CURRENCY_MAP[selectedOutCurrency].type === CurrencyType.CRYPTO
              ? "crypto"
              : "fiat"
          }
          onCreate={onCreateWallet}
        />

        <NewWalletModal
          isOpen={showWalletForm}
          close={() => setShowWalletForm(false)}
          callback={() => {
            toastSuccess("Wallet created successfully");
            setShowWalletForm(false);
          }}
        />

        <NewBankAccountModal
          isOpen={showBankAccountForm}
          close={() => setShowBankAccountForm(false)}
          callback={() => {
            // toast is on modal in TestLayout
            //toastSuccess("Bank account created successfully");
            setShowBankAccountForm(false);
          }}
        />
        <h5 className="font-manrope font-extrabold text-base pt-6">
          ✅ Benefits of Archway Virtual USD Account
        </h5>
        <ul className="list-disc list-inside indent-1">
          <li>
            <b>Automated Conversion</b> - Receive funds directly in{" "}
            {textCurrency} without manual exchanges.
          </li>
          <li>
            <b>Transparent Pricing</b> - No hidden fees and market exchange
            rates.
          </li>
          <li>
            <b>Universal Compatibility</b> - Compatible with major global
            platforms (<b>Upwork, Deel, PayPal, Fiverr,</b> etc.).
          </li>
        </ul>
        <h5 className="font-manrope font-extrabold text-base pt-6">💡 FAQs</h5>
        <div className="flex flex-col gap-1">
          <CollapsePanel
            title="Can I choose different currencies for payout?"
            className="py-1 px-2 text-xs"
            open={true}
          >
            Yes, you can select your preferred currency for payouts (currently
            supporting USDC on Polygon, INR, with more currencies coming soon).
          </CollapsePanel>
          <CollapsePanel
            title="Are there any hidden fees?"
            className="py-1 px-2 text-xs"
            open={false}
          >
            No hidden fees. We clearly display conversion rates before each
            transaction.
          </CollapsePanel>
          <CollapsePanel
            title="How quickly are funds converted and delivered?"
            className="py-1 px-2 text-xs"
            open={false}
          >
            Conversions happen instantly upon receipt. Funds are delivered
            immediately to your USDC wallet or within <b>24 hours</b> to your
            Indian bank account.
          </CollapsePanel>
        </div>
      </div>
    </TestLayout>
  );
};
